/**
 * public-stream-signalling.ts — PRI-496 Lane B streaming cutover.
 *
 * Drop-in replacement for the legacy SignallingClient that rides the PUBLIC
 * Streams API instead of the internal /v1/stream/signal plane:
 *
 *   create   → POST /v1/streams {prompt, options:{narrative:true}, recording:true}
 *              (SDK transport: Clerk JWT or anon pv_live_ key — the same
 *              triple-accept path an API customer uses)
 *   signal   → WS {stream.signaling.url}?token=… (Clerk JWT directly; pv_
 *              secret keys are NEVER placed on a URL — an ephemeral pvct_
 *              client token with the streams:signal scope is minted instead)
 *   ice      → stream.ice_servers from the create response (replaces the
 *              internal /v1/stream/turn-credentials fetch)
 *
 * The adapter exposes the EXACT legacy SignallingClientOptions callback
 * surface and translates both vocabularies:
 *
 *   legacy client → public: join → join · offer → offer · ice → ice ·
 *                           stop → end · ping → ping
 *   public server → legacy: queued → queued · ready → ready ·
 *                           metering → streaming · warning → quota_warning ·
 *                           result → metadata (timing flattened, answer
 *                           re-cased, narrative_update re-hydrated) ·
 *                           status → status (narrative-ordering vocabulary:
 *                           recalculating / prompt_context / combined_prompt) ·
 *                           prompt_updated → status prompt_update_ok ·
 *                           end {reason} → stopped / session_ended /
 *                           quota_exceeded / error
 *
 * Public-plane deltas (documented, accepted at cutover):
 *  * No pre-connect (connect/set_prompt vocabulary) — the PRI-281 fast path
 *    is legacy-only; useStreamSession skips preConnect() on this plane.
 *  * No mid-stream WS reconnect — the server finalizes a stream when its
 *    signaling socket closes, so a dropped WS ends the session (surfaced via
 *    onDisconnected). The legacy plane allowed silent rejoin.
 *  * PRIMATE_CFG is built server-side (buildInferencePrompt): plan budgets
 *    and user timezone are fixed to free-tier/UTC. narrative_update entries
 *    carry only {t_s, text} publicly — wall_clock is synthesized locally.
 *  * No daily-quota metering (credits instead): daily_remaining_s mirrors
 *    session_remaining_s in the mapped 'streaming' callback.
 *
 * Prompt updates mid-stream keep riding the WebRTC data channel (browser →
 * sidecar directly, identical on both planes); the resulting recalculating /
 * prompt_context / combined_prompt statuses flow back through this WS for
 * narrative-opted-in streams (PRI-531).
 */
import { sdk } from './sdk'
import { PrimateError } from '@primate-intelligence/sdk/browser'
import type { Stream } from '@primate-intelligence/sdk/browser'
import { getAuthToken } from './token'
import type {
  ClientMessage,
  InferenceResult,
  SignallingClientOptions,
  SignallingDiagnostics,
} from '../stream-signalling'

const PING_INTERVAL_MS = 10_000
const PONG_TIMEOUT_MS = 15_000
/** One retry when the previous session's stream is still finalizing server-side. */
const ACTIVE_RETRY_DELAY_MS = 1_500

export interface PublicStreamSignallingOptions extends SignallingClientOptions {
  /**
   * Fired with the create response's ice_servers BEFORE any offer is created
   * (onReady always arrives later) so the WebRTCClient can adopt the public
   * TURN credentials without the internal turn-credentials fetch.
   */
  onIceServers?: (servers: RTCIceServer[]) => void
}

interface PublicServerMessage {
  type: string
  [key: string]: unknown
}

/** Legacy UI renders 'Yes'/'No' verbatim; the public contract is lowercase. */
function toLegacyAnswerCase(answer: unknown): unknown {
  if (typeof answer !== 'string') return answer
  const a = answer.toLowerCase().trim()
  if (a === 'yes') return 'Yes'
  if (a === 'no') return 'No'
  return answer
}

function localWallClock(): string {
  try {
    return new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
  } catch {
    return new Date().toISOString()
  }
}

/**
 * Public detection frame → the legacy LiveInferenceResult dict the hook
 * consumes: flatten `timing` back to top level, restore legacy answer casing,
 * and re-hydrate the public {t_s, text} narrative_update into the legacy
 * NarrativeEntry shape (wall_clock synthesized from the local clock — entries
 * arrive in real time; window_start_elapsed_s = t_s).
 */
export function toLegacyInference(det: Record<string, unknown>, frameNum: number): InferenceResult {
  const { timing, narrative_update, answer, ...rest } = det
  const out: InferenceResult = {
    ...rest,
    ...(timing && typeof timing === 'object' ? (timing as Record<string, unknown>) : {}),
    frame_num: frameNum,
  }
  if (answer !== undefined) out.answer = toLegacyAnswerCase(answer) as string
  if (narrative_update && typeof narrative_update === 'object') {
    const nu = narrative_update as { t_s?: number; text?: string }
    if (typeof nu.text === 'string' && nu.text.length > 0) {
      const tS = typeof nu.t_s === 'number' && Number.isFinite(nu.t_s) ? nu.t_s : 0
      out.narrative_update = {
        wall_clock: localWallClock(),
        elapsed_s: tS,
        window_start_elapsed_s: tS,
        text: nu.text,
        type: 'detection',
      }
    }
  }
  return out
}

export class PublicStreamSignalling {
  private options: PublicStreamSignallingOptions
  private ws: WebSocket | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private pongTimer: ReturnType<typeof setTimeout> | null = null
  private stopped = false
  /** Server confirmed terminal ('end' received) — suppress onDisconnected on close. */
  private ended = false
  /** Recalculating watchdog — see the 'status' case in handleMessage. */
  private recalcTimer: ReturnType<typeof setTimeout> | null = null
  private lastElapsedS = 0
  private _auth = false
  // Terminal-disconnect diagnostics (PRI-421 parity with the legacy client).
  private everOpened = false
  private lastCloseCode: number | null = null
  private lastCloseReason: string | null = null

  /** The public stream id (st_…) once created — exposed for telemetry/tests. */
  streamId: string | null = null

  get isAuthenticated(): boolean { return this._auth }

  constructor(options: PublicStreamSignallingOptions) {
    this.options = options
  }

  /** Legacy-interface entry point. sessionId/timezone are legacy-plane concepts
   *  (the public stream id is server-issued; timezone is server-side UTC). */
  connect(_sessionId: string, prompt: string, _timezone: string): void {
    this.stopped = false
    this.ended = false
    void this.start(prompt)
  }

  /** PRI-281 pre-connect is legacy-only. Callers gate on the plane; fail loudly
   *  if one slips through rather than silently hanging. */
  connectNoPrompt(_sessionId: string, _timezone: string): void {
    this.options.onError('unsupported', 'Pre-connect is not supported on the public streams plane.')
  }

  /** Legacy fast-path prompt announce — public plane uses update_prompt. */
  setPromptViaWS(prompt: string): void {
    this.wsSend({ type: 'update_prompt', prompt })
  }

  setOptions(partial: Partial<SignallingClientOptions>): void {
    this.options = { ...this.options, ...partial }
  }

  /** Translate the legacy client vocabulary onto the public signaling WS. */
  send(msg: ClientMessage): void {
    switch (msg.type) {
      case 'offer': this.wsSend({ type: 'offer', sdp: msg.sdp }); return
      case 'ice': this.wsSend({ type: 'ice', candidate: msg.candidate }); return
      case 'stop': this.wsSend({ type: 'end' }); return
      case 'ping': this.wsSend({ type: 'ping' }); return
      case 'set_prompt': this.wsSend({ type: 'update_prompt', prompt: msg.prompt }); return
      // join is sent automatically on WS open; connect (pre-connect) unsupported.
      default: return
    }
  }

  disconnect(): void {
    this.stopped = true
    this.stopHeartbeat()
    if (this.recalcTimer) { clearTimeout(this.recalcTimer); this.recalcTimer = null }
    if (this.ws) {
      try { this.ws.close() } catch { /* already closed */ }
      this.ws = null
    }
  }

  // ── Create + connect flow ──────────────────────────────────────────────────

  private async start(prompt: string): Promise<void> {
    try {
      // Strip legacy client-side serializations — the public plane compiles the
      // prompt and builds PRIMATE_CFG server-side (buildInferencePrompt).
      const cleanPrompt = prompt.split('|||')[0].trim()
      const stream = await this.createStream(cleanPrompt)
      if (this.stopped) return
      this.streamId = stream.id
      if (Array.isArray(stream.ice_servers) && stream.ice_servers.length > 0) {
        this.options.onIceServers?.(stream.ice_servers as RTCIceServer[])
      }
      const signalingUrl = stream.signaling?.url
      if (!signalingUrl) {
        this.options.onError('internal_error', 'Stream created but no signaling URL was returned.')
        return
      }
      const token = await this.resolveWsToken(stream.id)
      if (this.stopped) return
      this.openWebSocket(`${signalingUrl}?token=${encodeURIComponent(token)}`)
    } catch (e) {
      if (this.stopped) return
      const { code, message } = toErrorParts(e)
      if (code === 'insufficient_credits') { this.options.onQuotaExceeded(); return }
      if (code === 'capacity_exhausted') { this.options.onError('queue_full', message); return }
      this.options.onError(code, message)
    }
  }

  private async createStream(prompt: string): Promise<Stream> {
    const body: Record<string, unknown> = {
      prompt,
      // Narrative + server-side recording retrieval opt-ins (PRI-510 / GAP-2).
      options: { narrative: true },
      recording: true,
    }
    try {
      return await this.postStream(body)
    } catch (e) {
      const { code, param } = toErrorParts(e)
      // Older server without PUBLIC_NARRATIVE_ENABLED: 'options' is an unknown
      // field. Degrade gracefully — stream without narrative rather than fail.
      if (code === 'validation_failed' && (param === 'options' || param === 'recording')) {
        const { options: _o, recording: _r, ...bare } = body
        return await this.postStream(bare)
      }
      // Previous session still finalizing server-side (WS close → finalize is
      // async). One delayed retry mirrors the legacy rejoin forgiveness.
      if (code === 'stream_already_active') {
        await new Promise<void>(r => setTimeout(r, ACTIVE_RETRY_DELAY_MS))
        return await this.postStream(body)
      }
      throw e
    }
  }

  private postStream(body: Record<string, unknown>): Promise<Stream> {
    // sdk.streams.create's typed params don't carry options/recording yet —
    // same transport, raw body (public contract fields per §4.8/PRI-510).
    return sdk.http.post<Stream>('/v1/streams', body)
  }

  /**
   * WS auth token: a Clerk session JWT rides the URL directly; pv_/pk_ secret
   * keys are NEVER accepted on the signaling WS (§18.1 — URLs leak), so anon
   * key sessions mint an ephemeral stream-bound pvct_ client token instead.
   */
  private async resolveWsToken(streamId: string): Promise<string> {
    const bearer = await getAuthToken()
    if (!bearer) throw Object.assign(new Error('Sign in (or start a trial) to stream.'), { code: 'unauthorized' })
    if (!/^p[vk]_/i.test(bearer)) return bearer // Clerk session JWT
    const ct = await sdk.clientTokens.create({ scopes: ['streams:signal'], stream_id: streamId })
    return ct.token
  }

  // ── WebSocket ──────────────────────────────────────────────────────────────

  private openWebSocket(url: string): void {
    const ws = new WebSocket(url)
    this.ws = ws
    this._auth = false

    ws.onopen = () => {
      this.everOpened = true
      this.options.onWsOpen?.()
      // Token was pre-verified at upgrade time — there is no auth_ok round
      // trip on the public plane. Surface auth immediately, then join.
      this._auth = true
      this.options.onAuthOk?.()
      this.startHeartbeat()
      this.wsSend({ type: 'join' })
    }

    ws.onmessage = (event) => {
      let msg: PublicServerMessage
      try { msg = JSON.parse(event.data as string) as PublicServerMessage } catch { return }
      this.handleMessage(msg)
    }

    ws.onerror = () => { /* onclose carries the diagnostics */ }

    ws.onclose = (e) => {
      this.lastCloseCode = e.code ?? null
      this.lastCloseReason = e.reason || null
      this.stopHeartbeat()
      // The server finalizes the stream when the socket closes — a reconnect
      // would hit resource_conflict. Surface the terminal disconnect instead.
      if (!this.stopped && !this.ended) {
        this.options.onDisconnected(this.diagnostics())
      }
    }
  }

  private diagnostics(): SignallingDiagnostics {
    return {
      everOpened: this.everOpened,
      everAuthed: this._auth,
      attempts: 0,
      lastCloseCode: this.lastCloseCode,
      lastCloseReason: this.lastCloseReason,
    }
  }

  private handleMessage(msg: PublicServerMessage): void {
    switch (msg.type) {
      case 'pong':
        if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null }
        return
      case 'queued':
        this.options.onQueued(Number(msg.position ?? 0), Number(msg.estimated_start_s ?? 0))
        return
      case 'ready':
        // recording_max_minutes is a legacy-plane knob → default (30) applies.
        this.options.onReady(null)
        return
      case 'answer':
        this.options.onAnswer(String(msg.sdp ?? ''))
        return
      case 'ice':
        this.options.onIce(msg.candidate as RTCIceCandidateInit)
        return
      case 'live':
        // Legacy plane had no explicit live event — metering/metadata drive the
        // UI state machine. Intentionally ignored.
        return
      case 'metering': {
        const elapsed = Number(msg.elapsed_s ?? 0)
        const remaining = Number(msg.session_remaining_s ?? msg.balance_s ?? 0)
        this.lastElapsedS = elapsed
        // No daily quota on the public plane (credits meter instead) — mirror
        // the session reservation into the legacy daily slot.
        this.options.onStreaming(elapsed, remaining, remaining)
        return
      }
      case 'warning': {
        if (msg.code === 'no_media_frames') {
          // Media watchdog diagnostic — not a quota event. The hook's own
          // first-frame watchdog owns this failure mode; log only.
          console.warn('[PublicStream] no_media_frames warning:', JSON.stringify(msg))
          return
        }
        const remaining = Number(msg.remaining_s ?? 0)
        this.options.onQuotaWarning(remaining, remaining)
        return
      }
      case 'result': {
        const detections = Array.isArray(msg.detections) ? msg.detections : []
        const det = detections[0]
        if (det && typeof det === 'object') {
          this.options.onMetadata?.(Number(msg.frame_num ?? 0), toLegacyInference(det as Record<string, unknown>, Number(msg.frame_num ?? 0)))
        }
        return
      }
      case 'status': {
        // Narrative-ordering vocabulary (recalculating / prompt_context /
        // combined_prompt) — forwarded for narrative-opted-in streams (PRI-531).
        const status = String(msg.status ?? '')
        this.options.onStatus?.(status, String(msg.message ?? ''))
        // The public plane does NOT forward prompt_update_ok/_failed (internal
        // vocabulary), but the legacy UI state machine needs one of them to
        // leave 'recalculating'. Success path: prompt_context is the last
        // pre-ok message — synthesize the ok right after it. Failure/duplicate
        // path: no prompt_context ever arrives — an 8s watchdog reverts to
        // live via prompt_update_failed semantics.
        if (status === 'prompt_context') {
          if (this.recalcTimer) { clearTimeout(this.recalcTimer); this.recalcTimer = null }
          this.options.onStatus?.('prompt_update_ok', '')
        } else if (status === 'recalculating') {
          if (this.recalcTimer) clearTimeout(this.recalcTimer)
          this.recalcTimer = setTimeout(() => {
            this.recalcTimer = null
            this.options.onStatus?.('prompt_update_ok', '')
          }, 8_000)
        }
        return
      }
      case 'prompt_updated':
        this.options.onStatus?.('prompt_update_ok', '')
        return
      case 'end':
        this.ended = true
        this.mapEnd(String(msg.reason ?? ''))
        return
      case 'error': {
        const code = String(msg.code ?? 'error')
        const message = String(msg.message ?? '')
        // update_prompt compile failures are transient — legacy vocabulary is a
        // prompt_update_failed status (revert-to-live), not a session error.
        if (code === 'parse_failed' || code === 'validation_failed') {
          this.options.onStatus?.('prompt_update_failed', message)
          return
        }
        if (code === 'capacity_exhausted') { this.ended = true; this.options.onError('queue_full', message); return }
        this.options.onError(code, message)
        return
      }
      default:
        return
    }
  }

  /** Public end{reason} → the legacy terminal callback vocabulary. */
  private mapEnd(reason: string): void {
    switch (reason) {
      case 'completed':
      case 'canceled':
        this.options.onStopped(this.lastElapsedS, 'user_stop')
        return
      case 'timeout':
        this.options.onSessionEnded(this.lastElapsedS, 0)
        return
      case 'insufficient_credits':
        this.options.onQuotaExceeded()
        return
      case 'ice_failed':
      case 'media_timeout':
        this.options.onError(reason, 'The video connection could not be established. Check your network and retry.')
        return
      default:
        // 'error' and anything unknown → PRI-424 server-error surface.
        this.options.onStopped(this.lastElapsedS, 'server_error')
        return
    }
  }

  // ── Heartbeat ──────────────────────────────────────────────────────────────

  private startHeartbeat(): void {
    this.pingTimer = setInterval(() => {
      this.wsSend({ type: 'ping' })
      this.pongTimer = setTimeout(() => {
        console.warn('[PublicStream] Pong timeout — closing socket')
        try { this.ws?.close() } catch { /* already closed */ }
      }, PONG_TIMEOUT_MS)
    }, PING_INTERVAL_MS)
  }

  private stopHeartbeat(): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null }
    if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null }
  }

  private wsSend(obj: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj))
    }
  }
}

function toErrorParts(e: unknown): { code: string; message: string; param?: string } {
  if (e instanceof PrimateError) {
    return {
      code: e.code ?? 'error',
      message: e.rawMessage || `API error: ${e.status}`,
      param: e.param ?? e.errors?.[0]?.param ?? undefined,
    }
  }
  const err = e as { code?: string; message?: string; param?: string }
  return { code: err?.code ?? 'error', message: err?.message ?? 'Streaming failed to start.', param: err?.param }
}
